import { FC, ReactNode } from 'react';

import { FloatingTabs } from '@dbt-labs/sourdough';

import { useQueryParamOption } from '@/hooks/queryParamOption';
import { buildTypePredicate } from '@/util/type';

interface PanelTabsProps {
  publicModelCount?: number;
  relationshipsCount?: number;
  children: (tabType: TabType) => ReactNode;
}

const tabTypes = ['general', 'publicModels', 'relationships'] as const;
type TabType = (typeof tabTypes)[number];

type TabInfo = {
  type: TabType;
  count?: number;
};

const tabNameMap: Record<TabType, string> = {
  general: 'General',
  publicModels: 'Public Models',
  relationships: 'Relationships',
};

const isTabType = buildTypePredicate(tabTypes);

const getTabType = (input: string | undefined | null): TabType | null => {
  return isTabType(input) ? input : null;
};

export const ProjectPanelTabs: FC<PanelTabsProps> = (props: PanelTabsProps) => {
  const { children } = props;
  const [tabParam, setTabParam] = useQueryParamOption({
    param: 'tab',
    options: tabTypes,
    validateOption: getTabType,
  });
  const tabs: TabInfo[] = [
    { type: 'general' },
    { type: 'publicModels', count: props.publicModelCount },
    { type: 'relationships', count: props.relationshipsCount },
  ]; // TODO: relationships needs a count
  const activeTab = tabParam ? { type: tabParam } : tabs[0];

  return (
    <div>
      <FloatingTabs>
        {tabs.map((tabDetails) => (
          <div key={tabNameMap[tabDetails.type]} className="whitespace-nowrap">
            <FloatingTabs.Tab
              text={tabNameMap[tabDetails.type]}
              id={tabDetails.type}
              isActive={activeTab.type === tabDetails.type}
              onClick={(tab: TabType) => {
                setTabParam(tab);
              }}
              count={tabDetails.count}
            />
          </div>
        ))}
      </FloatingTabs>

      <>{activeTab.type && <div>{children(activeTab.type)}</div>}</>
    </div>
  );
};
